import React, { createContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  fetchConversations as fetchConversationsAPI,
  fetchMessages as fetchMessagesAPI,
  fetchOlderMessages as fetchOlderMessagesAPI
} from '../services/api/conversationApi';

/**
 * ConversationContext
 * 
 * Global state management for chat history feature.
 * 
 * Manages:
 * - List of user's conversations
 * - Active conversation selection
 * - Message cache (avoid redundant API calls)
 * - Pagination state (cursor-based)
 * - Loading and error states
 * 
 * Design Decisions:
 * - Messages cached per conversation to avoid refetching
 * - Message cache auto-evicts old conversations (LRU style)
 * - Cursor-based pagination for stability
 * - Separate loading states for conversations vs messages
 * 
 * @typedef {Object} Conversation
 * @property {string} conversation_id
 * @property {string} title
 * @property {string} agent_id
 * @property {string} last_message_preview
 * @property {number} message_count
 * @property {string} created_at
 * @property {string} updated_at
 */
export const ConversationContext = createContext();

/**
 * ConversationProvider Component
 * 
 * Wrap your app with this to enable conversation history functionality.
 * 
 * @example
 * <ConversationProvider>
 *   <ChatPage />
 * </ConversationProvider>
 */
export const ConversationProvider = ({ children }) => {
  // ============================================================
  // STATE MANAGEMENT
  // ============================================================

  // List of conversations (from API)
  const [conversations, setConversations] = useState([]);

  // Currently selected conversation (null = new chat)
  const [activeConversationId, setActiveConversationId] = useState(null);

  // Cache: { [conversationId]: { messages, prev_cursor, has_older, isLoading } }
  const [messageCache, setMessageCache] = useState({});

  // Pagination state for conversations list
  const [conversationCursor, setConversationCursor] = useState(null);

  // Loading states
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [, setIsLoadingMessages] = useState(false);

  // Error state
  const [error, setError] = useState(null);

  // Ref to track if initial fetch is done
  const initialFetchDone = useRef(false);

  // ============================================================
  // UTILITY FUNCTIONS
  // ============================================================

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Reset all conversation state (call on logout)
   * Clears conversations, message cache, active conversation, and allows re-fetch
   */
  const resetConversations = useCallback(() => {
    setConversations([]);
    setActiveConversationId(null);
    setMessageCache({});
    setConversationCursor(null);
    setIsLoadingConversations(false);
    setIsLoadingMessages(false);
    setError(null);
    initialFetchDone.current = false;
  }, []);

  /**
   * Add messages to cache for a conversation
   * Handles both initial fetch and loading older messages
   * 
   * @param {string} conversationId - Conversation to cache
   * @param {Array} newMessages - Messages to add
   * @param {string} prevCursor - Cursor for loading older
   * @param {boolean} hasOlder - Are there older messages?
   * @param {boolean} isInitial - Is this the initial fetch?
   */
  const addMessagesToCache = useCallback((
    conversationId,
    newMessages,
    prevCursor,
    hasOlder,
    isInitial = true
  ) => {
    setMessageCache(prev => {
      const existing = prev[conversationId] || { messages: [], prev_cursor: null };

      // If initial fetch, replace all messages
      // If loading older, prepend messages to top
      const messages = isInitial
        ? newMessages
        : [...newMessages, ...existing.messages];

      return {
        ...prev,
        [conversationId]: {
          messages,
          prev_cursor: prevCursor,
          has_older: hasOlder,
          isLoading: false
        }
      };
    });
  }, []);

  /**
   * Mark conversation messages as loading
   */
  const setMessageLoading = useCallback((conversationId, isLoading) => {
    setMessageCache(prev => ({
      ...prev,
      [conversationId]: {
        ...(prev[conversationId] || {}),
        isLoading
      }
    }));
  }, []);

  /**
   * Clear old cache entries (keep last 5 conversations in memory)
   * Prevents memory leak from too many cached conversations
   */
  const evictOldCache = useCallback(() => {
    setMessageCache(prev => {
      const entries = Object.entries(prev);
      if (entries.length > 5) {
        // Keep only the last 5 conversations
        const entriesToKeep = entries.slice(-5);
        return Object.fromEntries(entriesToKeep);
      }
      return prev;
    });
  }, []);

  // ============================================================
  // API ACTIONS
  // ============================================================

  /**
   * Fetch initial list of conversations on mount
   */
  const fetchConversations = useCallback(async (cursor = null) => {
    try {
      setError(null);
      setIsLoadingConversations(true);

      const result = await fetchConversationsAPI({
        limit: 15,
        cursor
      });

      // Sort conversations by updated_at, latest first
      const sortByLatest = (convs) =>
        [...convs].sort((a, b) => {
          const toMs = (v) => {
            if (typeof v === 'number') return v < 1e12 ? v * 1000 : v;
            return new Date(v).getTime() || 0;
          };
          return toMs(b.updated_at) - toMs(a.updated_at);
        });

      // If this is not the initial fetch, append to existing and re-sort
      if (cursor) {
        setConversations(prev => sortByLatest([
          ...prev,
          ...result.conversations
        ]));
      } else {
        setConversations(sortByLatest(result.conversations));
      }

      setConversationCursor(result.next_cursor);
    } catch (err) {
      console.error('[ConversationContext] Error fetching conversations:', err);
      setError({
        type: err.type || 'UNKNOWN',
        message: err.message
      });
    } finally {
      setIsLoadingConversations(false);
    }
  }, []);

  /**
   * Fetch more conversations (pagination)
   */
  const fetchMoreConversations = useCallback(async () => {
    if (!conversationCursor) {
      console.log('[ConversationContext] No more conversations to load');
      return;
    }

    await fetchConversations(conversationCursor);
  }, [conversationCursor, fetchConversations]);
  /**
   * Set active conversation and fetch its messages
   * 
   * @param {string|null} conversationId - Conversation to activate (null for new chat)
   * @param {boolean} skipFetch - If true, don't fetch messages (for new conversations)
   */
  const activateConversation = useCallback(async (conversationId, skipFetch = false) => {
    try {
      setError(null);
      setActiveConversationId(conversationId);

      // If null (new chat), don't fetch messages
      if (!conversationId) {
        return;
      }

      // If skipFetch is true, don't fetch (messages are in local chatLog)
      if (skipFetch) {
        return;
      }

      // Check if messages already cached
      if (messageCache[conversationId]) {
        return;
      }

      // Fetch messages from API
      setMessageLoading(conversationId, true);

      const result = await fetchMessagesAPI(conversationId, { limit: 20 });

      addMessagesToCache(
        conversationId,
        result.messages,
        result.prev_cursor,
        result.has_older,
        true // isInitial
      );

      evictOldCache();
    } catch (err) {
      console.error('[ConversationContext] Error activating conversation:', err);
      setError({
        type: err.type || 'UNKNOWN',
        message: err.message
      });

      // If conversation not found, remove from list
      if (err.type === 'NOT_FOUND') {
        setConversations(prev =>
          prev.filter(conv => conv.conversation_id !== conversationId)
        );
      }
    }
  }, [messageCache, addMessagesToCache, setMessageLoading, evictOldCache]);

  /**
   * Load older messages for a conversation (scroll up pagination)
   * 
   * @param {string} conversationId - Conversation to load older messages for
   */
  const loadOlderMessages = useCallback(async (conversationId) => {
    try {
      const cached = messageCache[conversationId];
      
      if (!cached) {
        console.warn('[ConversationContext] No cached messages for:', conversationId);
        return;
      }

      if (!cached.prev_cursor) {
        console.log('[ConversationContext] No older messages available');
        return;
      }

      if (cached.isLoading) {
        console.log('[ConversationContext] Already loading older messages');
        return;
      }

      setMessageLoading(conversationId, true);

      const result = await fetchOlderMessagesAPI(
        conversationId,
        cached.prev_cursor,
        20
      );

      addMessagesToCache(
        conversationId,
        result.messages,
        result.prev_cursor,
        result.has_older,
        false // not initial (prepend)
      );
    } catch (err) {
      console.error('[ConversationContext] Error loading older messages:', err);
      setError({
        type: err.type || 'UNKNOWN',
        message: err.message
      });
    }
  }, [messageCache, addMessagesToCache, setMessageLoading]);

  /**
   * Add new message(s) to cache for a conversation
   * Called after a message is sent to update the message cache
   * 
   * @param {string} conversationId - Conversation ID
   * @param {Array|Object} messages - Message or array of messages to add
   */
  const addMessageToCache = useCallback((conversationId, messages) => {
    if (!conversationId || !messages) return;
    
    const messagesToAdd = Array.isArray(messages) ? messages : [messages];
    
    setMessageCache(prev => {
      const cached = prev[conversationId];
      
      // Build updated entry - ensure messages is always an array
      const updatedEntry = cached
        ? {
            ...cached,
            messages: [...(Array.isArray(cached.messages) ? cached.messages : []), ...messagesToAdd]
          }
        : {
            messages: messagesToAdd,
            prev_cursor: null,
            has_older: false,
            isLoading: false
          };
      
      // Reconstruct cache: remove this conversation and add it at the end
      // This ensures recently updated conversations don't get evicted
      const { [conversationId]: _, ...rest } = prev;
      
      const newCache = {
        ...rest,
        [conversationId]: updatedEntry
      };
      
      return newCache;
    });
  }, []);

  /**
   * Update conversation list when new message is sent
   * Called from ChatPage after message is successfully sent and conversation_id received
   */
  const updateConversationList = useCallback((newConversation) => {
    setConversations(prev => {
      // Check if conversation already exists
      const exists = prev.some(c => c.conversation_id === newConversation.conversation_id);
      
      if (exists) {
        // Move to top and update
        return [
          newConversation,
          ...prev.filter(c => c.conversation_id !== newConversation.conversation_id)
        ];
      } else {
        // Add to top
        return [newConversation, ...prev];
      }
    });
  }, []);

  /**
   * Initialize conversation context on mount
   * Only fetch conversations if user is authenticated with valid token
   */
  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      
      // Check if user has valid session
      const checkAuthAndFetch = async () => {
        try {
          const agentType = localStorage.getItem('agentType');
          const tokenExpiry = localStorage.getItem('tokenExpiry');
          
          // If no stored session, skip fetch
          if (!agentType || !tokenExpiry) {
            console.log('[ConversationContext] No stored session, skipping fetch');
            return;
          }
          
          // Check if token is expired
          const expiryTime = parseInt(tokenExpiry) * 1000;
          const now = Date.now();
          if (now >= expiryTime) {
            console.log('[ConversationContext] Token expired, skipping fetch');
            // Clear stale data
            localStorage.removeItem('agentType');
            localStorage.removeItem('tokenExpiry');
            return;
          }
          
          // Token is valid, fetch conversations
          await fetchConversations();
        } catch (err) {
          console.error('[ConversationContext] Error during auth check:', err);
        }
      };
      
      checkAuthAndFetch();
    }
  }, [fetchConversations]);

  // ============================================================
  // CONTEXT VALUE
  // ============================================================

  // Extract active messages to a variable so it's properly tracked
  const activeMessages = useMemo(
    () => messageCache[activeConversationId]?.messages || [],
    [messageCache, activeConversationId]
  );

  // Memoize the context value to ensure it only changes when necessary
  const value = useMemo(() => ({
    // State
    conversations,
    activeConversationId,
    messageCache,
    conversationCursor,
    isLoadingConversations,
    error,

    // Selectors
    activeConversation: conversations.find(
      c => c.conversation_id === activeConversationId
    ),
    
    // Convenience getters for chat state
    conversationId: activeConversationId,
    activeMessages,

    // Actions
    activateConversation,
    fetchMoreConversations,
    loadOlderMessages,
    updateConversationList,
    addMessageToCache,
    clearError,
    fetchConversations,
    resetConversations
  }), [conversations, activeConversationId, messageCache, conversationCursor, isLoadingConversations, error, activeMessages, activateConversation, fetchMoreConversations, loadOlderMessages, updateConversationList, addMessageToCache, clearError, fetchConversations, resetConversations]);

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
};

export default ConversationContext;
